import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { ReportProducts } from "@/components/products/ReportProducts";
import { periodLabel, useFilters } from "@/context/FilterContext";
import { useFeature } from "@/context/AuthContext";

export const FORMATS = [["pdf", "PDF"], ["xlsx", "Excel"], ["csv", "CSV"]];
export const SCHEDULES = { none: "Nessun invio automatico", weekly: "Ogni lunedì alle 7:00", monthly: "Il giorno 1 di ogni mese alle 7:00" };

const Checks = ({ items, value, onChange, prefix }) => (
  <div className="grid gap-1 sm:grid-cols-2">
    {items.map((it) => (
      <label key={it.key} className={`flex items-center gap-2 rounded px-1 py-1 text-sm ${it.disabled ? "opacity-40" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`${prefix}-${it.key}`}>
        <Checkbox disabled={it.disabled} checked={value.includes(it.key)} onCheckedChange={() => onChange(value.includes(it.key) ? value.filter((x) => x !== it.key) : [...value, it.key])} />
        <span className="truncate">{it.label}</span>
      </label>
    ))}
  </div>
);

const Field = ({ title, children }) => <div><div className="mb-1.5 text-xs font-semibold text-slate-700">{title}</div>{children}</div>;

export const ReportDialog = ({ report, onClose, onSaved }) => {
  const { filters } = useFilters();
  const aiOn = useFeature("ai");
  const [f, setF] = useState(report);
  const [opts, setOpts] = useState(null);
  useEffect(() => { setF(report); if (report && !opts) api.get("/reports/options").then((r) => setOpts(r.data)); }, [report]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const save = async () => {
    try {
      const body = { ...f, period: filters.period };
      const { data } = f.id ? await api.put(`/reports/${f.id}`, body) : await api.post("/reports", body);
      toast.success("Report salvato"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!report} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid="report-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Modifica report" : "Nuovo report"}</DialogTitle></DialogHeader>
        {!opts ? <div className="py-8 text-center text-sm text-slate-400">Caricamento...</div> : <div className="space-y-4">
          <label className="block text-xs text-slate-500">Nome del report<Input className="mt-1" data-testid="report-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800" data-testid="report-filters-note">
            Il report usa i filtri attivi ora ({periodLabel(filters)}{filters.stores.length ? `, ${filters.stores.length} punti vendita` : ""}{filters.cities.length + filters.provs.length ? ", località" : ""}{filters.segment ? `, ${filters.segmentLabel || "segmento"}` : ""}). I periodi "ultimi N giorni" si aggiornano a ogni invio.
          </div>
          <Field title="Contenuti"><Checks prefix="report-section" value={f.sections} onChange={(v) => setF({ ...f, sections: v })} items={opts.sections.map((s) => ({ key: s.key, label: s.label, disabled: !s.allowed }))} /></Field>
          {f.sections.includes("products") && <Field title="Prodotti da analizzare"><ReportProducts value={f.products} onChange={(v) => setF({ ...f, products: v })} /></Field>}
          {f.sections.includes("ai") && <Field title="Analisi IA da includere">
            <div className="max-h-36 overflow-y-auto rounded-lg border border-slate-100 p-1"><Checks prefix="report-ai" value={f.ai_ids} onChange={(v) => setF({ ...f, ai_ids: v })} items={opts.analyses.map((a) => ({ key: a.id, label: a.title || "Analisi" }))} /></div>
          </Field>}
          <Field title="Formati"><div className="flex gap-4"><Checks prefix="report-format" value={f.formats} onChange={(v) => setF({ ...f, formats: v })} items={FORMATS.map(([k, l]) => ({ key: k, label: l }))} /></div></Field>
          {aiOn && <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="report-ai-summary"><Checkbox checked={f.ai_summary !== false} onCheckedChange={(v) => setF({ ...f, ai_summary: !!v })} />Riassunto IA in cima con i 3 risultati chiave del periodo</label>}
          <Field title="Destinatari email (utenti del pannello)">
            <div className="max-h-36 overflow-y-auto rounded-lg border border-slate-100 p-1"><Checks prefix="report-recipient" value={f.recipients} onChange={(v) => setF({ ...f, recipients: v })} items={opts.users.map((u) => ({ key: u.id, label: `${u.name} · ${u.email}` }))} /></div>
          </Field>
          <Field title="Invio automatico">
            <Select value={f.schedule} onValueChange={(v) => setF({ ...f, schedule: v })}>
              <SelectTrigger data-testid="report-schedule-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{Object.entries(SCHEDULES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>}
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button data-testid="report-save-button" disabled={!opts} className="bg-blue-600 hover:bg-blue-700" onClick={save}>Salva report</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
